// Chat Service for AI Tutor API Integration
import { v4 as uuidv4 } from "uuid";
import { aiAxiosInstance } from "../axios/aiAxiosConfig";
import { AI_TUTOR_API } from "../constants/ai-api-constants";
import { getCurrentUserFromLocalStorage } from "../helpers/localStorageHelper";

const AI_API_BASE_URL = process.env.AI_API_ENDPOINT;

class ChatService {
  constructor() {
    this.currentUser = getCurrentUserFromLocalStorage();
    this.userId = this.getUserId();
    this.serverUserId = null;
  }

  // Initialize with server-side user data
  initializeUser(serverUserData) {
    if (serverUserData && serverUserData.userId) {
      this.serverUserId = serverUserData.userId;
      this.userEmail = serverUserData.userEmail;
      this.userName = serverUserData.userName;
    }
  }

  // Get effective user ID (server-side if available, otherwise local)
  getEffectiveUserId() {
    return this.serverUserId || this.userId;
  }

  // Get or create user ID
  getUserId() {
    return this.currentUser.ssoId || "";
  }

  // Reset conversation (create new conversation ID)
  resetConversation() {
    this.conversationId = uuidv4();
    sessionStorage.setItem("ai_tutor_conversation_id", this.conversationId);
  }

  // Send message to AI API with streaming support
  // async sendMessage(
  //   message,
  //   history = [],
  //   language = "English",
  //   conversationId
  // ) {
  //   try {
  //     const requestBody = {
  //       user_id: this.getEffectiveUserId(),
  //       conversation_id: conversationId,
  //       message: message,
  //       language: language,
  //       history: history,
  //     };

  //     // Get current user for authorization
  //     const currentUser = getCurrentUserFromLocalStorage();
  //     const authToken = currentUser?.token;

  //     const response = await fetch(
  //       `${AI_API_BASE_URL}${AI_TUTOR_API.CHAT_STREAM_API}`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Accept: "text/event-stream",
  //           ...(authToken && { Authorization: `Bearer ${authToken}` }),
  //         },
  //         body: JSON.stringify(requestBody),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     return response;
  //   } catch (error) {
  //     console.error("Error sending message to AI API:", error);
  //     throw error;
  //   }
  // }

  // Send message with files to AI API using FormData
  async sendMessage(
    message,
    history = [],
    language = "English",
    conversationId,
    files = []
  ) {
    try {
      // Get current user for authorization
      const currentUser = getCurrentUserFromLocalStorage();
      const authToken = currentUser?.token;

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      
      // Add text fields
      formData.append("user_id", this.getEffectiveUserId());
      formData.append("conversation_id", conversationId || "");
      formData.append("message", message);
      formData.append("language", language);
      formData.append("history", JSON.stringify(history));

      // Add files
      if (files && files.length > 0) {
        files.forEach((file, index) => {
          formData.append("files", file, file.name);
        });
      }

      const headers = {
        Accept: "text/event-stream",
      };

      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(
        `${AI_API_BASE_URL}${AI_TUTOR_API.CHAT_STREAM_API}`,
        {
          method: "POST",
          headers: headers,
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error("Error sending message with files to AI API:", error);
      throw error;
    }
  }

  // Process streaming response
  async *processStreamingResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim() === "") continue;

          // Handle Server-Sent Events format
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            // Skip heartbeat or end signals
            if (data === "[DONE]" || data === "") continue;

            try {
              const parsed = JSON.parse(data);
              yield parsed;
            } catch (e) {
              // If not JSON, treat as plain text
              yield { content: data };
            }
          } else {
            // Handle plain text responses
            yield { content: line.trim() };
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Fallback method for non-streaming response
  async sendMessageFallback(
    message,
    history = [],
    language = "English",
    conversationId
  ) {
    try {
      const requestBody = {
        user_id: this.getEffectiveUserId(),
        conversation_id: conversationId,
        message: message,
        language: language,
        history: history,
      };

      // Get current user for authorization
      const currentUser = getCurrentUserFromLocalStorage();
      const authToken = currentUser?.token;

      const response = await fetch(
        `${AI_API_BASE_URL}${AI_TUTOR_API.CHAT_STREAM_API}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error with fallback API call:", error);
      throw error;
    }
  }

  // Save learning plan to server
  async saveLearningPlan(planData) {
    try {
      const response = await aiAxiosInstance.post(
        AI_TUTOR_API.SAVE_LEARNING_PLAN_API,
        {
          userId: this.getEffectiveUserId(),
          userEmail: this.userEmail,
          userName: this.userName,
          planData: planData,
          createdAt: new Date().toISOString(),
        }
      );

      return response.data;
    } catch (error) {
      console.error("Failed to save learning plan:", error);
      throw error;
    }
  }

  // Load learning plans from server
  async loadLearningPlans() {
    try {
      const response = await aiAxiosInstance.get(
        AI_TUTOR_API.LOAD_LEARNING_PLANS_API,
        {
          params: {
            userId: this.getEffectiveUserId(),
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Failed to load learning plans:", error);
      throw error;
    }
  }
}

export const getConversationByUserId = async () => {
  const chatService = new ChatService();
  const response = await aiAxiosInstance.get(AI_TUTOR_API.CONVERSATIONS_API, {
    params: {
      user_id: chatService.getEffectiveUserId(),
    },
  });
  return response.data;
};

// Create new conversation
export const createConversation = async (conversationData) => {
  const chatService = new ChatService();
  const userId = chatService.getEffectiveUserId();
  try {
    const response = await aiAxiosInstance.post(
      AI_TUTOR_API.CONVERSATIONS_API,
      {
        user_id: userId,
        ...conversationData,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating conversation:", error);
    throw error;
  }
};

// Delete conversation by ID
export const deleteConversation = async (conversationId) => {
  try {
    const response = await aiAxiosInstance.delete(
      `${AI_TUTOR_API.CONVERSATIONS_API}/${conversationId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting conversation:", error);
    throw error;
  }
};

export const renameConversation = async (
  conversationId,
  conversationTitle = ""
) => {
  try {
    const safeTitle = encodeURIComponent(conversationTitle || "");
    const url = AI_TUTOR_API.RENAME_CONVERSATION_API.replace(
      "{conversation_id}",
      conversationId
    ).replace("{title}", safeTitle);
    const response = await aiAxiosInstance.put(url);
    return response.data;
  } catch (error) {
    console.error("Error renaming conversation:", error);
    throw error;
  }
};

export const getConversationMessagesById = async (conversationId) => {
  const response = await aiAxiosInstance.get(
    `${AI_TUTOR_API.CONVERSATIONS_API}/${conversationId}/messages`
  );
  return response.data;
};

export default new ChatService();
